/**  Custom components stores services types constant */
import axiosInstance from '../../services/http.api';
import { getStatusLabel, STATUS} from '../../utils/statusHandler';
import { STORAGE_KEYS, StorageService } from '../../utils/storageService';
import { CERTIFICATE_STATUS, CertificateData } from '../Certificates/certificates.types';
import {DEFAULT_ERROR_MSG} from './../../utils/constants';
import { CertificateSite, DescriptionOfProductData, FSCClaimLabelData, LogoRequestConstants, LogoRequestDetails, ProductType, ProductTypeString, RequestDetailsData, UserCommentedData } from './logoRequest.Type';

export const logoRequestService = {
    /**
     * Logo request detail data
     * @param logoRequestId 
     * @returns 
     */
    fetchLogoRequestDetails: async (logoRequestId: string): Promise<LogoRequestDetails> => {
        try {

            const res = await axiosInstance.get<any>(`/v1/LOGORequest/${logoRequestId}`);

            if (!res.data.isSuccess) {
                // Throw an error with custom message
                throw new Error(res.data.message || DEFAULT_ERROR_MSG);
            }

            let data = res.data.logoRequest;

            const selectedSites: string[] = res.data.siteNames ?? [];

            // request detail data
            let reqDetailData : RequestDetailsData = {
                companyName: (data.scsCertificate?.account?.name) ? data.scsCertificate?.account?.name : "",
                siteName: selectedSites,
                jobName: (data.jobName) ? data.jobName : "",
                fscTrademarkCode: (data.fscTrademarkLicenseCode) ? data.fscTrademarkLicenseCode : "",
                scsCertificationNumber: (data.fscCertificationNumber) ? data.fscCertificationNumber : "",
                jobNumber: (data.jobNumber) ? data.jobNumber : "",
                fscLicenceCode: (data.fscLicenseCode) ? data.fscLicenseCode : "",
                allSiteMetaData : [{id:"N/A",label:"No data found",value:"N/A"}],
                labelingAgreementPath : data.labelingAgreementPath,
            }
        
            // Product description
            let descriptionOfProductData : DescriptionOfProductData = {
                description : (data.description) ? data.description : "",
            }
        
            // Commented data
            let commentedData : UserCommentedData = {
                comments : (data.comment) ? data.comment : "",
            }

             // FSC Claim label data
            let fscClaimLabelData : FSCClaimLabelData = {
                fscClaimLabel : (data.fscClaimLabel) ? data.fscClaimLabel : "",
            }

            const certificateData = StorageService.get<CertificateData>(STORAGE_KEYS.CERTIFICATE);

            //data.status = STATUS.CHANGE_REQUESTED;
            
            // Logo request data
            let logoRequestData : LogoRequestDetails = {
                requestId : logoRequestId,
                requestDetailsData : reqDetailData,
                productDescription : descriptionOfProductData,
                fscClaimLabel : fscClaimLabelData,
                commentedData : commentedData,
                status : data.status,
                requestType: data.requestType,
                requestName: (data.name) ? data.name : "",
                isReadOnly : (data.status === STATUS.SUBMITTED || data.status === STATUS.APPROVED || data.status == STATUS.CANCELLED) ? true : false,
                documentShareFilePath : data.folderPath,
                agreementShareFilePath : data.fscAggreementPath,
                submittedDate : data.submittedDate,
                displayStatus : getStatusLabel(data.status),
                customFolderPath : data.customFolderPath,
                certificate : certificateData
            };

            // If certificate status is not certified then logo request should display in read only mode
            if(certificateData?.status != CERTIFICATE_STATUS.CERTIFIED){
                logoRequestData.isReadOnly = true;
            }

            return logoRequestData;

        } catch (error: any) {
            throw new Error(error?.response?.data?.message || LogoRequestConstants.LOGO_REQUEST_FETCH_FAIL_MSG);
        }

    },

    /**
     * Create logo request
     * @param requestType 
     * @param certificateId 
     * @param licenseCodeLabelingAgreement 
     * @param fscAgreementPath 
     * @returns 
     */
    createLogoRequest: async (requestType: ProductType,certificateId: string, licenseCodeLabelingAgreement: string, fscAgreementPath: string): Promise<LogoRequestDetails> => {
        try {
            let reqType = '';
            if(requestType == ProductType.OnProduct){
              reqType = ProductTypeString.OnProduct;  
            }else if(requestType == ProductType.OffProduct){
              reqType = ProductTypeString.OffProduct;
            }

            if(reqType === ''){
               throw new Error(DEFAULT_ERROR_MSG);
            }
            const reuqestData = {'requestType':reqType,'certificateId':certificateId};
            const res = await axiosInstance.post<any>('/v1/LOGORequest/create',reuqestData);

            if (!res.data.success) {
                // Throw an error with custom message
                throw new Error(res.data.message || DEFAULT_ERROR_MSG);
            }

            let data = res.data.data;
            
            // process sites
            const certificateSite: CertificateSite[] = data.sites.map((siteItem: any) => ({
                id: siteItem?.siteName,
                label: siteItem?.siteName,
                value: siteItem?.siteName
            }));

            // request detail data
            let reqDetailData : RequestDetailsData = {
                labelingAgreementPath : "",
                companyName: (data.companyName) ? data.companyName : "",
                siteName: [],
                jobName: (data.jobName) ? data.jobName : "",
                fscTrademarkCode: (data.trademarkLicenseCode) ? data.trademarkLicenseCode : "",
                scsCertificationNumber: (data.certificateNumber) ? data.certificateNumber : "",
                jobNumber: (data.jobNumber) ? data.jobNumber : "",
                fscLicenceCode: (data.fscLicenseCode) ? data.fscLicenseCode : "",
                allSiteMetaData : (certificateSite && certificateSite.length > 0) ? certificateSite : [{id:"N/A",label:"No data found",value:"N/A"}],
            }
        
            // Product description
            let descriptionOfProductData : DescriptionOfProductData = {
                description : (data.description) ? data.description : "",
            }
        
            // Commented data
            let commentedData : UserCommentedData = {
                comments : (data.comment) ? data.comment : "",
            }

             // FSC Claim label data
            let fscClaimLabelData : FSCClaimLabelData = {
                fscClaimLabel : (data.fscClaimLabel) ? data.fscClaimLabel : "",
            }

            const certificateData = StorageService.get<CertificateData>(STORAGE_KEYS.CERTIFICATE);
        
            // Logo request data
            let logoRequestData : LogoRequestDetails = {
                requestId : (data.id) ? data.id : "",
                requestDetailsData : reqDetailData,
                productDescription : descriptionOfProductData,
                fscClaimLabel : fscClaimLabelData,
                commentedData : commentedData,
                status : STATUS.DRAFT,
                requestType: reqType,
                requestName: (data.logoName) ? data.logoName : "",
                isReadOnly : (data.status === STATUS.SUBMITTED || data.status === STATUS.APPROVED || data.status == STATUS.CANCELLED) ? true : false,
                documentShareFilePath : "",
                agreementShareFilePath : "",
                submittedDate : "",
                displayStatus :  getStatusLabel(STATUS.DRAFT),
                customFolderPath : "",
                certificate : certificateData
            };

            // If certificate status is not certified then logo request should display in read only mode
            if(certificateData?.status != CERTIFICATE_STATUS.CERTIFIED){
                logoRequestData.isReadOnly = true;
            }

            return logoRequestData;

        } catch (error: any) {
            throw new Error(error?.response?.data?.message || LogoRequestConstants.LOGO_REQUEST_CREATE_FAIL_MSG);
        }
    },

   /**
    * Update logo request
    * @param requestDetailsData 
    * @param status 
    * @param submittedDate 
    * @returns 
    */
    updateLogoRequest: async (requestDetailsData : LogoRequestDetails, status:string, submittedDate:string): Promise<any> => {
        try {
            let submitDate = null;
            let folderPath = null;
            let fscAggreementPath = null;
            let fscLabelingAgreementPath = null;

            const siteNames = requestDetailsData.requestDetailsData.siteName ?? [];

            if(requestDetailsData.requestDetailsData.fscLicenceCode === 'Yes'){
                fscLabelingAgreementPath = requestDetailsData.requestDetailsData.labelingAgreementPath;
            }

            let fscLicenseCode = requestDetailsData.requestDetailsData.fscLicenceCode;
            if(!fscLicenseCode || fscLicenseCode == ""){
               fscLicenseCode = "No";
            }

            let agreementShareFilePath = requestDetailsData.agreementShareFilePath;
            if(agreementShareFilePath && agreementShareFilePath != ""){
               fscAggreementPath = agreementShareFilePath;
            }

            let documentFolderPath = requestDetailsData.documentShareFilePath;
            if(documentFolderPath && documentFolderPath != ""){
               folderPath = documentFolderPath;
            }

            if(submittedDate == ""){
                submitDate = null;
            }else{
                submitDate = submittedDate;
            }

            const reuqestData = {'siteName':siteNames, 'jobName':requestDetailsData.requestDetailsData.jobName, 'jobNumber':requestDetailsData.requestDetailsData.jobNumber, 'fscClaimLabel':requestDetailsData.fscClaimLabel.fscClaimLabel, 'descriptionOfProduct':requestDetailsData.productDescription.description, "status": status, "fscLicenseCode":fscLicenseCode, "labelingAgreementPath":fscLabelingAgreementPath,"folderPath":folderPath,"fscAggreementPath":fscAggreementPath,"submittedDate":submitDate};
            const res = await axiosInstance.put<any>(`/v1/LOGORequest/update/${requestDetailsData.requestId}`,reuqestData);

            if (!res.data.isSuccess) {
                // Throw an error with custom message
                throw new Error(res.data?.errors?.message || DEFAULT_ERROR_MSG);
            }

            return res.data;

        } catch (error: any) {
            throw new Error(error?.response?.data?.message || LogoRequestConstants.LOGO_REQUEST_SUBMITTED_FAIL_MSG);
        }
    },

     /**
     * Get customer sites
     * @param certificateId 
     * @returns 
     */
    fetchCustomerSites: async (certificateId: string): Promise<CertificateSite[]> => {
        try {

            if(certificateId === ''){
               throw new Error(DEFAULT_ERROR_MSG);
            }

            const res = await axiosInstance.get<any>(`/v1/LOGORequest/customerSites/${certificateId}`);

            if (!res.data.isSuccess) {
                // Throw an error with custom message
                throw new Error(res.data.message || DEFAULT_ERROR_MSG);
            }

            const data = res.data.certifiedSitesModels || [];
            const certificateSite: CertificateSite[] = data.filter((siteItem: any) => siteItem?.customerSite?.name)
            .map((siteItem: any) => ({
                id: siteItem.customerSite.name,
                label: siteItem.customerSite.name,
                value: siteItem.customerSite.name,
            }));

            return certificateSite;

        } catch (error: any) {
            throw new Error(error?.response?.data?.message || LogoRequestConstants.CUSTOMER_SITE_FETCH_FAIL_MSG);
        }
    }
};